/**
 * Cart drawer estimated-delivery messages.
 *
 * Fills each line item's `.cart-item-delivery-message` placeholder with an ETA
 * derived from the variant's location provider, reusing the shared
 * window.OGKicksDelivery calculation module. Re-runs on every cart re-render
 * (add to cart / quantity change) via a MutationObserver on <cart-drawer>,
 * because those flows replace the line item markup with fresh server HTML.
 */
(() => {
    if (window.__cartDeliveryMessageLoaded) return;
    window.__cartDeliveryMessageLoaded = true;

    const PLACEHOLDER_SELECTOR = '.cart-item-delivery-message';
    // Dataset flag marking an already-processed placeholder, so our own DOM
    // writes do not retrigger the observer into an infinite loop.
    const RENDERED_FLAG = 'deliveryRendered';

    const CALENDAR_ICON =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
        'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<rect x="3" y="4" width="18" height="18" rx="2"></rect>' +
        '<line x1="16" y1="2" x2="16" y2="6"></line>' +
        '<line x1="8" y1="2" x2="8" y2="6"></line>' +
        '<line x1="3" y1="10" x2="21" y2="10"></line></svg>';

    const renderPlaceholder = (el) => {
        if (el.dataset[RENDERED_FLAG]) return;

        const delivery = window.OGKicksDelivery;
        if (!delivery) return;

        el.dataset[RENDERED_FLAG] = 'true';

        const provider = el.getAttribute('data-variant-provider') || '';
        const estimate = delivery.getDeliveryEstimate(provider);

        if (!estimate) {
            // No known provider for this variant: leave the row empty/hidden.
            el.classList.remove('is-visible');
            el.innerHTML = '';
            return;
        }

        // Same promise as the PDP: the next-day copy only when the calculated
        // date is literally tomorrow, otherwise the date itself.
        const text =
            estimate.kind === 'tomorrow'
                ? delivery.NEXT_DAY_COPY
                : `Estimated delivery: <strong>${estimate.text}</strong>`;

        el.innerHTML =
            `<span class="cart-item-delivery-message__icon">${CALENDAR_ICON}</span>` +
            `<span class="cart-item-delivery-message__text">${text}</span>`;
        el.classList.add('is-visible');
    };

    const renderAll = (root) => {
        (root || document).querySelectorAll(PLACEHOLDER_SELECTOR).forEach(renderPlaceholder);
    };

    const observeDrawer = () => {
        // <cart-drawer> is the stable ancestor: cart.js replaces cart-drawer-items
        // and the footer inside it, and cart-drawer.js rewrites #CartDrawer's inner
        // HTML on add to cart. Both surface here as childList mutations.
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) return;

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'childList' && mutation.addedNodes.length) {
                    renderAll(drawer);
                    return;
                }
            }
        });

        observer.observe(drawer, { childList: true, subtree: true });
        renderAll(drawer);
    };

    const init = () => {
        renderAll(document);
        observeDrawer();
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
        init();
    }
})();
